import { useState } from 'react';
import { Button, Table, Drawer, Input, Select, Alert, Badge, useToast } from '@dreamtree-org/twreact-ui';
import { Plus, Pencil } from 'lucide-react';
import { PageHeader, QueryState, EmptyState } from '@/components';
import { errorText } from '@/lib/format';
import { useQuery } from '@/lib/useQuery';
import { useCan } from '@rbac/useCan';
import Can from '@rbac/Can';
import { useProductTypes, useStoreCustomFields, modelAttributes, formFromAttributes, attributesFromForm, validateAttributes, specLine, defaultSpecs } from '@/lib/productTypes';
import { useCrudList, useCrudWrite } from '../hooks';
import { modelName } from '../lib/catalog';
import SpecFields from '../components/SpecFields';
import BrandSelect from '../components/BrandSelect';
import ModelImages from '../components/ModelImages';
import NoProductTypes, { noTypesChosen } from '../components/NoProductTypes';

const BASE = ['brand_id', 'brand', 'series', 'model_name', 'model_code', 'category_id'];

/** Field-level errors from a 422 `details` list ("attributes.storage_gb" → storage_gb). */
function detailErrors(ex) {
  const details = (ex && (ex.details || (ex.response && ex.response.data && ex.response.data.details))) || [];
  return Object.fromEntries(details.map((d) => [String(d.path).replace(/^attributes\./, ''), d.message]));
}

/** Catalog library (PRD S-02): model templates of any product type (D32), separate from physical units. */
export default function ProductModels({ embedded = false }) {
  const can = useCan();
  const { toast } = useToast();
  const registry = useProductTypes();
  const store = useQuery('storeProductTypes', {}, { select: (r) => (r && r.data) || [] });
  const list = useCrudList('ProductModel', { with: ['Category'], orderBy: { column: 'brand', direction: 'asc' }, limit: 500 });
  const categories = useCrudList('Category', { limit: 500 });
  const write = useCrudWrite();
  const [editing, setEditing] = useState(null); // null | { id?, product_type, base fields, specs: {} }
  const [errors, setErrors] = useState({});

  const enabled = (store.data || []).filter((t) => t.enabled).map((t) => t.key);
  // A type with the store's own fields (ADR 0009 §4): the form shows them after the platform's.
  const customFields = useStoreCustomFields();
  const typeOf = (key) => customFields.withFields(registry.byKey[key] || null);

  const open = (row) => {
    setErrors({});
    if (row) {
      const type = typeOf(row.product_type);
      setEditing({
        id: row.id, product_type: row.product_type || '',
        ...Object.fromEntries(BASE.map((k) => [k, row[k] == null ? '' : String(row[k])])),
        specs: formFromAttributes(type, modelAttributes(row)),
      });
    } else {
      // A new model starts on the store's first type (no default type, ADR 0009 §1).
      const first = enabled[0] || '';
      setEditing({ product_type: first, ...Object.fromEntries(BASE.map((k) => [k, ''])), specs: defaultSpecs(typeOf(first)) });
    }
  };
  const set = (patch) => setEditing((e) => ({ ...e, ...patch }));
  const setSpecs = (patch) => setEditing((e) => ({ ...e, specs: { ...e.specs, ...patch } }));
  const changeType = (key) => setEditing((e) => ({ ...e, product_type: key, category_id: '', specs: defaultSpecs(typeOf(key)) }));

  const save = async () => {
    const type = typeOf(editing.product_type);
    const e = validateAttributes(type, editing.specs);
    if (!type) e.product_type = 'Pick a product type';
    if (!editing.brand_id) e.brand_id = 'Pick a brand, or type a new one to add it';
    if (!String(editing.model_name || '').trim()) e.model_name = 'Model is required';
    setErrors(e);
    if (Object.keys(e).length) return;
    const payload = {
      brand_id: Number(editing.brand_id), series: editing.series.trim() || null, model_name: editing.model_name.trim(),
      model_code: editing.model_code.trim() || null, category_id: editing.category_id ? Number(editing.category_id) : null,
      product_type: editing.product_type, attributes: attributesFromForm(type, editing.specs),
    };
    try {
      if (editing.id) await write.update('ProductModel', editing.id, payload);
      else await write.create('ProductModel', payload);
      toast.success(editing.id ? 'Model updated' : 'Model created', modelName(editing));
      setEditing(null);
      list.reload();
    } catch (ex) {
      setErrors({ ...detailErrors(ex), form: errorText(ex) });
    }
  };

  const type = editing && typeOf(editing.product_type);
  const typeOptions = registry.types
    .filter((t) => enabled.includes(t.key) || (editing && t.key === editing.product_type))
    .map((t) => ({ value: t.key, label: t.label }));
  const categoryOptions = (categories.data || [])
    .filter((c) => !editing || c.product_type === editing.product_type)
    .map((c) => ({ value: String(c.id), label: c.name }));

  return (
    <div className="space-y-4">
      <PageHeader
        title={embedded ? undefined : 'Models'}
        subtitle="Reusable make/model templates for every kind of device you sell. Physical units reference a model."
        actions={<Can module="catalog" action="create"><Button leftIcon={<Plus size={16} />} onClick={() => open(null)} disabled={!enabled.length}>New model</Button></Can>}
      />
      {/* No product types chosen yet: no models can exist or be made, so no list or form (as on Import). */}
      {noTypesChosen(store.data) ? <NoProductTypes /> : (
      <QueryState query={list}>
        {(rows) =>
          rows.length ? (
            <Table
              data={rows}
              columns={[
                { key: 'name', label: 'Model', render: (m) => <span className="font-medium">{modelName(m)}</span> },
                { key: 'product_type', label: 'Type', render: (m) => <Badge variant="secondary" size="sm">{(typeOf(m.product_type) || {}).label || m.product_type || '—'}</Badge> },
                { key: 'specs', label: 'Specs', render: (m) => specLine(typeOf(m.product_type), modelAttributes(m)) || '—' },
                { key: 'category', label: 'Category', render: (m) => (m.Category && m.Category.name) || '—', hideOnMobile: true },
              ]}
              actions={can('catalog', 'update') ? [{ name: 'edit', label: 'Edit', icon: <Pencil size={14} />, inline: true, onClick: ({ row }) => open(row) }] : []}
              replaceDefaultActions
              withAction={can('catalog', 'update')}
              onRowClick={can('catalog', 'update') ? (m) => open(m) : undefined}
              showSerial={false}
              showColumnPicker={false}
              globalSearch
              pagination
              pageSize={25}
              onReload={list.reload}
            />
          ) : (
            <EmptyState title="No models yet" action={<Can module="catalog" action="create"><Button onClick={() => open(null)}>Create a model</Button></Can>}>
              Models hold the shared specs of a device (storage, screen, processor…) for many units.
            </EmptyState>
          )
        }
      </QueryState>
      )}

      <Drawer
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={editing && editing.id ? 'Edit model' : 'New model'}
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} loading={write.loading}>Save</Button>
          </div>
        }
      >
        {editing ? (
          <div className="space-y-4">
            {errors.form ? <Alert variant="error">{errors.form}</Alert> : null}
            <Select
              label="Product type"
              required
              options={typeOptions}
              value={editing.product_type}
              onChange={(v) => v && changeType(v)}
              helperText={editing.id ? 'Changing the type clears the specs below.' : 'Add more types under Settings → What you sell.'}
              closeOnSelect
              error={errors.product_type}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <BrandSelect required typeKey={editing.product_type} value={editing.brand_id} onChange={(id, name) => set({ brand_id: id, brand: name })} error={errors.brand_id} />
              <Input label="Series" value={editing.series} onChange={(e) => set({ series: e.target.value })} />
              <Input label="Model" required value={editing.model_name} onChange={(e) => set({ model_name: e.target.value })} error={errors.model_name} />
              <Input label="Model code" value={editing.model_code} onChange={(e) => set({ model_code: e.target.value })} />
              <Select
                label="Category"
                placeholder="None"
                options={categoryOptions}
                value={editing.category_id}
                onChange={(v) => set({ category_id: v || '' })}
                allowClear
                closeOnSelect
                error={errors.category_id}
              />
            </div>
            {type ? (
              <div className="space-y-2">
                <p className="text-sm font-semibold">{type.label} specs</p>
                <SpecFields type={type} value={editing.specs} onChange={setSpecs} errors={errors} />
              </div>
            ) : null}
            {/* Catalog images (MERCH-81, D95): a saved model only, since files attach to its row. */}
            {editing.id && can('catalog', 'view') ? <ModelImages modelId={editing.id} /> : null}
          </div>
        ) : null}
      </Drawer>
    </div>
  );
}
